export async function* readStream(response: Response): AsyncGenerator<string> {
	const reader = response.body!.getReader();
	const decoder = new TextDecoder("utf-8");
	let buffer = "";

	try {
		while (true) {
			const { done, value } = await reader.read();

			if (done) {
				// Process any remaining buffer content
				if (buffer.trim()) {
					const processed = processLine(buffer.trim());
					if (processed) yield processed;
				}
				break;
			}

			buffer += decoder.decode(value, { stream: true });

			// Split by newlines but keep processing complete lines only
			const lines = buffer.split("\n");

			// Process all complete lines (all except the last one)
			for (let i = 0; i < lines.length - 1; i++) {
				const line = lines[i].trim();
				if (!line) continue; // Skip empty lines

				const processed = processLine(line);
				if (processed) yield processed;
			}

			// Keep the last incomplete line in the buffer
			buffer = lines[lines.length - 1];
		}
	} finally {
		reader.releaseLock();
	}
}

function processLine(line: string): string | null {
	// Skip non-data lines
	if (!line.startsWith("data:")) {
		// Handle ping messages or other non-data lines if needed
		if (line.includes("ping")) {
			return null; // Skip ping messages
		}
		return null;
	}

	// Handle double "data: data:" format
	if (line.startsWith("data: data:")) {
		return line.slice("data: data:".length).trim();
	}

	// Handle single "data:" format
	if (line.startsWith("data:")) {
		const content = line.slice("data:".length).trim();

		// Handle the nested JSON structure for format 1
		// Check if it's the wrapped format: {"data": "..."}
		if (content.startsWith('{"data":')) {
			try {
				const parsed = JSON.parse(content);
				if (parsed.data !== undefined) {
					return parsed.data;
				}
			} catch {
				// If parsing fails, return as-is
				return content;
			}
		}

		return content;
	}

	return null;
}

// export async function* readStream(response: Response): AsyncGenerator<string> {
// 	const reader = (await response.body)!.getReader();
// 	let buffer = "";
// 	try {
// 		while (true) {
// 			const { done, value } = await reader.read();

// 			if (done) {
// 				break;
// 			}

// 			const val = new TextDecoder("utf-8").decode(value);
// 			buffer += val;

// 			const lines = buffer.split("\n");
// 			for (let i = 0; i < lines?.length - 1; i++) {
// 				const line = lines[i].trim();
// 				if (line.startsWith("data: data:")) {
// 					yield line.slice("data: data:"?.length).trim();
// 				} else if (line.startsWith("data:")) {
// 					yield line.slice("data:"?.length).trim();
// 				}
// 			}

// 			buffer = lines[lines?.length - 1];
// 		}
// 	} finally {
// 		reader.releaseLock();
// 	}
// }

// Converts a Uint8Array to a lowercase hex string
const bytesToHex = (bytes: Uint8Array): string =>
	Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");

// Generates a 24-char hex string shaped like a MongoDB ObjectId:
// 4 bytes timestamp + 5 bytes random + 3 bytes random (in place of a process counter).
// Intended for client-side use only — not guaranteed to match server-side ObjectId uniqueness semantics.
export function generateObjectId(): string {
	// 4 bytes timestamp (seconds since epoch)
	const timestamp = Math.floor(Date.now() / 1000)
		.toString(16)
		.padStart(8, "0");

	// 8 bytes of CSPRNG output covering both the random and pseudo-increment segments
	const randomBytes = new Uint8Array(8);
	crypto.getRandomValues(randomBytes);

	return timestamp + bytesToHex(randomBytes);
}

// ── Personas module utilities ──────────────────────────────────────────────

/** Conditionally join class names (drop falsy values). */
export function cn(...inputs: (string | undefined | null | false | 0)[]): string {
	return inputs.filter(Boolean).join(" ");
}

// Generates a unique id with an optional prefix, backed by crypto.randomUUID()
// to satisfy SAST rules around insecure randomness and guarantee collision resistance.
export function uid(prefix = "id"): string {
	return `${prefix}_${crypto.randomUUID()}`;
}

/** Return current time as an ISO string. */
export function nowISO(): string {
	return new Date().toISOString();
}

/** Format an ISO timestamp as a human-readable relative time string. */
export function formatAgo(iso: string): string {
	if (!iso) return "";
	const time = new Date(iso).getTime();
	if (Number.isNaN(time)) return "";
	const diff = Date.now() - time;
	const mins = Math.floor(diff / 60000);
	if (mins < 1) return "Just now";
	if (mins < 60) return `${mins}m ago`;
	const hrs = Math.floor(mins / 60);
	if (hrs < 24) return `${hrs}h ago`;
	return `${Math.floor(hrs / 24)}d ago`;
}
